import { useEffect, useState } from "react";
import PageHeader from "@/components/base/PageHeader";
import { SkeletonBlock } from "@/components/base/Skeleton";
import { listSessions, getSessionReport, type SessionReport } from "@/api/alpha";

const PERIODS = ["30天", "90天"];

export default function Reports() {
  const [period, setPeriod] = useState("30天");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [hasSession, setHasSession] = useState(true);
  const [report, setReport] = useState<SessionReport | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { sessions } = await listSessions();
        const s = sessions.find((x) => x.status === "running") || sessions[0];
        if (!s) {
          if (alive) setHasSession(false);
          return;
        }
        const data = await getSessionReport(s.id);
        if (!alive) return;
        setHasSession(true);
        setReport(data);
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : "加载失败");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // --- Loading ---
  if (loading) {
    return (
      <div className="pb-[80px]">
        <PageHeader title="收益报表" />
        <div className="px-4 space-y-4 mt-4">
          <SkeletonBlock className="h-9 w-40 rounded-full" />
          <SkeletonBlock className="h-[200px] rounded-xl" />
          <div className="grid grid-cols-2 gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <SkeletonBlock key={i} className="h-[78px] rounded-xl" />
            ))}
          </div>
          <SkeletonBlock className="h-[160px] rounded-xl" />
        </div>
      </div>
    );
  }

  // --- Error ---
  if (error) {
    return (
      <div className="pb-[80px]">
        <PageHeader title="收益报表" />
        <div className="px-4 mt-20 text-center">
          <i className="ri-error-warning-line text-4xl text-foreground-300" />
          <p className="text-sm text-foreground-500 mt-3">{error}</p>
        </div>
      </div>
    );
  }

  // --- No session ---
  if (!hasSession || !report) {
    return (
      <div className="pb-[80px]">
        <PageHeader title="收益报表" />
        <div className="px-4 mt-20 text-center">
          <i className="ri-bar-chart-grouped-line text-4xl text-foreground-300" />
          <p className="text-sm text-foreground-500 mt-3">还没有数据</p>
          <p className="text-xs text-foreground-400 mt-1">请先在 AI 看盘开启模拟盘</p>
        </div>
      </div>
    );
  }

  const o = report.overview;
  const days = period === "90天" ? 90 : 30;

  // Net-value curve: plot absolute totalAsset, sliced to the chosen window.
  const curve = report.curve || [];
  const netValueCurve = curve.slice(-days).map((p) => p.totalAsset);
  const maxNav = netValueCurve.length ? Math.max(...netValueCurve) : 0;
  const minNav = netValueCurve.length ? Math.min(...netValueCurve) : 0;
  const navRange = maxNav - minNav || 1;

  const metrics = {
    totalReturn: o.totalReturn,
    winRate: o.winRate, // -1 sentinel = 暂无平仓
    maxDrawdown: o.maxDrawdown,
    sharpe: o.sharpe,
    sharpeSampleN: o.sharpeSampleN,
    totalTrades: o.totalTrades,
    winTrades: o.winTrades,
    loseTrades: Math.max(0, o.closedTrades - o.winTrades),
  };

  // 个股贡献度 — sort highest realised/unrealised PnL first (profits then losses).
  const stockContributions = [...report.byStock]
    .sort((a, b) => b.pnlAmt - a.pnlAmt)
    .map((b) => ({
      name: b.name,
      code: b.tickerCode,
      rate: b.pnlPct,
      // isHolding distinguishes 持仓中 (floating mark-to-market PnL) from
      // 已平仓 (locked realised PnL) so the bar isn't read as settled.
      holding: b.isHolding === true,
      type: b.pnlPct >= 0 ? ("profit" as const) : ("loss" as const),
    }));

  const sectorDistribution = report.sectorDistribution.map((d) => ({
    name: d.industry,
    percent: Math.round(d.weight),
  }));

  const monthlyWinRate = report.monthlyWinRate.map((m) => ({
    month: m.month,
    rate: Math.round(m.winRate),
  }));

  // 关键决策 — 最新 AI 交易信号
  const decisions = (report.keyDecisions || []).slice(0, 20);

  const actionColor: Record<string, string> = {
    "买入": "text-[#E11D48] bg-[#E11D48]/8",
    "加仓": "text-[#E11D48] bg-[#E11D48]/8",
    "卖出": "text-[#10B981] bg-[#10B981]/8",
    "减仓": "text-[#F59E0B] bg-[#F59E0B]/10",
    "持有": "text-foreground-600 bg-background-100",
    "观望": "text-foreground-600 bg-background-100",
  };
  const shortAction = (a: string) => a;

  return (
    <div className="pb-[80px]">
      <PageHeader title="收益报表" />

      {/* Period Switcher */}
      <div className="px-4 mb-4">
        <div className="flex bg-background-100 rounded-full p-1 w-fit">
          {PERIODS.map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-6 py-2 text-sm rounded-full font-medium transition-all cursor-pointer whitespace-nowrap ${
                period === p ? "bg-white text-foreground-950" : "text-foreground-500"
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* Net Value Curve */}
      <div className="px-4 mb-4">
        <div className="bg-white rounded-xl p-4">
          <h3 className="text-sm font-semibold text-foreground-950 mb-3">资产净值曲线</h3>
          <div className="relative h-[160px]">
            {netValueCurve.length >= 1 ? (
              <svg viewBox="0 0 300 160" className="w-full h-full" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="navGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2563EB" stopOpacity="0.15" />
                    <stop offset="100%" stopColor="#2563EB" stopOpacity="0" />
                  </linearGradient>
                </defs>
                {(() => {
                  const n = netValueCurve.length;
                  const pts = netValueCurve.map((v, i) => {
                    const x = n > 1 ? (i / (n - 1)) * 300 : 150;
                    const y = 150 - ((v - minNav) / navRange) * 140;
                    return `${i === 0 ? "M" : "L"} ${x} ${y}`;
                  }).join(" ");
                  return (
                    <>
                      <path d={pts} fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d={`${pts} L 300 160 L 0 160 Z`} fill="url(#navGrad)" />
                    </>
                  );
                })()}
              </svg>
            ) : (
              <div className="h-full flex items-center justify-center">
                <p className="text-xs text-foreground-400">数据积累中</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="px-4 mb-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white rounded-xl p-4">
            <p className="text-xs text-foreground-400">累计收益</p>
            <p className={`text-xl font-bold ${metrics.totalReturn >= 0 ? "text-[#E11D48]" : "text-[#10B981]"}`}>
              {metrics.totalReturn >= 0 ? "+" : ""}{metrics.totalReturn.toFixed(2)}%
            </p>
          </div>
          <div className="bg-white rounded-xl p-4">
            <p className="text-xs text-foreground-400">胜率</p>
            <p className="text-xl font-bold text-foreground-950">
              {metrics.winRate < 0 ? "暂无平仓" : `${metrics.winRate.toFixed(1)}%`}
            </p>
          </div>
          <div className="bg-white rounded-xl p-4">
            <p className="text-xs text-foreground-400">最大回撤</p>
            <p className="text-xl font-bold text-[#10B981]">{metrics.maxDrawdown.toFixed(2)}%</p>
          </div>
          <div className="bg-white rounded-xl p-4">
            <p className="text-xs text-foreground-400">夏普比率</p>
            <p className="text-xl font-bold text-foreground-950">
              {metrics.sharpeSampleN <= 2 ? "样本不足" : metrics.sharpe.toFixed(2)}
            </p>
          </div>
          <div className="bg-white rounded-xl p-4 col-span-2">
            <p className="text-xs text-foreground-400">总交易数</p>
            <div className="flex items-baseline gap-2 mt-1">
              <p className="text-xl font-bold text-foreground-950">{metrics.totalTrades}</p>
              <span className="text-xs text-[#E11D48]">{metrics.winTrades}盈</span>
              <span className="text-xs text-[#10B981]">{metrics.loseTrades}亏</span>
            </div>
          </div>
        </div>
      </div>

      {/* 盈亏明细 (realized / unrealized split) */}
      {(o.unrealizedPnL != null || o.realizedPnL != null) && (
        <div className="px-4 mb-4">
          <div className="bg-white rounded-xl p-4">
            <h3 className="text-sm font-semibold text-foreground-950 mb-3">盈亏明细</h3>
            <div className="grid grid-cols-2 gap-3">
              {o.unrealizedPnL != null && (
                <div className="bg-background-50 rounded-xl p-3">
                  <p className="text-[10px] text-foreground-400">浮动盈亏 (持仓)</p>
                  <p className={`text-base font-bold mt-1 ${o.unrealizedPnL >= 0 ? "text-[#E11D48]" : "text-[#10B981]"}`}>
                    {o.unrealizedPnL >= 0 ? "+" : ""}¥{Math.abs(o.unrealizedPnL).toLocaleString("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
              )}
              {o.realizedPnL != null && (
                <div className="bg-background-50 rounded-xl p-3">
                  <p className="text-[10px] text-foreground-400">已实现盈亏 (落袋)</p>
                  <p className={`text-base font-bold mt-1 ${o.realizedPnL >= 0 ? "text-[#E11D48]" : "text-[#10B981]"}`}>
                    {o.realizedPnL >= 0 ? "+" : ""}¥{Math.abs(o.realizedPnL).toLocaleString("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
              )}
              {o.feeTotal != null && o.feeTotal > 0 && (
                <div className="bg-background-50 rounded-xl p-3">
                  <p className="text-[10px] text-foreground-400">手续费 (累计)</p>
                  <p className="text-base font-bold mt-1 text-foreground-950">
                    ¥{o.feeTotal.toLocaleString("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
              )}
              {o.avgConfidence != null && (
                <div className="bg-background-50 rounded-xl p-3">
                  <p className="text-[10px] text-foreground-400">平均置信度</p>
                  <p className="text-base font-bold mt-1 text-foreground-950">
                    {(o.avgConfidence / 5 * 100).toFixed(0)}%
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Stock Contribution */}
      <div className="px-4 mb-4">
        <div className="bg-white rounded-xl p-4">
          <h3 className="text-sm font-semibold text-foreground-950 mb-3">个股贡献度</h3>
          {stockContributions.length === 0 ? (
            <p className="text-xs text-foreground-400 py-2">暂无交易记录</p>
          ) : (
            <div className="space-y-2.5">
              {stockContributions.map((s) => (
                <div key={s.code} className="flex items-center gap-3">
                  <span className="w-16 flex items-center gap-1 min-w-0">
                    <span className="text-xs text-foreground-700 truncate">{s.name}</span>
                    {s.holding && (
                      <span className="text-[9px] leading-none text-foreground-400 border border-background-200 rounded px-1 py-0.5 shrink-0">
                        持仓
                      </span>
                    )}
                  </span>
                  <div className="flex-1 h-2 bg-background-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${s.type === "profit" ? "bg-[#E11D48]" : "bg-[#10B981]"}`}
                      style={{ width: `${Math.min(100, Math.abs(s.rate) * 3)}%` }}
                    />
                  </div>
                  <span className={`text-xs font-semibold w-16 text-right ${s.type === "profit" ? "text-[#E11D48]" : "text-[#10B981]"}`}>
                    {s.type === "profit" ? "+" : ""}{s.rate.toFixed(1)}%
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Sector Distribution */}
      <div className="px-4 mb-4">
        <div className="bg-white rounded-xl p-4">
          <h3 className="text-sm font-semibold text-foreground-950 mb-3">行业分布</h3>
          {sectorDistribution.length === 0 ? (
            <p className="text-xs text-foreground-400 py-2">暂无持仓</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {sectorDistribution.map((s) => (
                <div key={s.name} className="text-center">
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-1"
                    style={{ background: `conic-gradient(#2563EB ${s.percent * 3.6}deg, #f1f5f9 0deg)` }}
                  >
                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
                      <span className="text-xs font-bold text-foreground-950">{s.percent}%</span>
                    </div>
                  </div>
                  <span className="text-[10px] text-foreground-500">{s.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 关键决策 — 最新 AI 交易信号 */}
      {decisions.length > 0 && (
        <div className="px-4 mb-4">
          <div className="bg-white rounded-xl p-4">
            <h3 className="text-sm font-semibold text-foreground-950 mb-3">关键决策</h3>
            <div className="space-y-2">
              {decisions.slice(0, 10).map((d, i) => (
                <div key={`${d.tickerCode || d.ticker}-${d.createdAt}-${i}`} className="flex items-start gap-3 py-2 border-b border-background-100 last:border-0">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-xs font-semibold text-foreground-950">{d.ticker}</span>
                      {d.tickerCode && (
                        <span className="text-[9px] text-foreground-400">{d.tickerCode}</span>
                      )}
                      <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${actionColor[d.action] || "text-foreground-500 bg-background-100"}`}>
                        {shortAction(d.action)}
                      </span>
                    </div>
                    <p className="text-[11px] text-foreground-600 leading-relaxed line-clamp-2">{d.summary}</p>
                  </div>
                  <div className="flex flex-col items-end gap-0.5 shrink-0">
                    <div className="flex items-center gap-0.5">
                      {Array.from({ length: 5 }, (_, si) => (
                        <i key={si} className={`text-[8px] ${si < (d.confidence || 0) ? "ri-star-fill text-accent-500" : "ri-star-line text-foreground-200"}`} />
                      ))}
                    </div>
                    <span className="text-[9px] text-foreground-400">{d.createdAt ? new Date(d.createdAt * 1000).toLocaleString("zh-CN", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""}</span>
                  </div>
                </div>
              ))}
              {decisions.length > 10 && (
                <p className="text-[10px] text-foreground-400 text-center pt-1">仅展示最近 10 条决策</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Monthly Win Rate */}
      <div className="px-4 mb-4">
        <div className="bg-white rounded-xl p-4">
          <h3 className="text-sm font-semibold text-foreground-950 mb-3">月度胜率趋势</h3>
          {monthlyWinRate.length === 0 ? (
            <p className="text-xs text-foreground-400 py-2">暂无数据</p>
          ) : (
            <div className="flex items-end justify-between h-[100px] gap-2">
              {monthlyWinRate.map((m) => (
                <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-[10px] font-semibold text-foreground-700">{m.rate}%</span>
                  <div
                    className="w-full rounded-t-md transition-all"
                    style={{
                      height: `${m.rate}%`,
                      background: m.rate >= 60
                        ? "linear-gradient(180deg, #2563EB, #60A5FA)"
                        : m.rate >= 50
                        ? "linear-gradient(180deg, #F59E0B, #FBBF24)"
                        : "linear-gradient(180deg, #E11D48, #F87171)",
                    }}
                  />
                  <span className="text-[10px] text-foreground-400">{m.month}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Export — no backend export/share endpoint yet; kept as an explicit
          disabled placeholder rather than a button that silently does nothing. */}
      <div className="px-4 mb-4">
        <button
          disabled
          className="w-full py-3 bg-white rounded-xl text-sm text-foreground-300 font-medium flex items-center justify-center gap-2 border border-background-200 cursor-not-allowed"
        >
          <i className="ri-download-2-line" />
          导出分享 · 敬请期待
        </button>
      </div>
    </div>
  );
}
